/**
 * userProfile.js — Shared helpers for the clinician profile fields on users/{uid}.
 *
 * Structured profile fields (all on users/{uid}, not userSettings):
 *   - firstName:     string
 *   - lastName:      string
 *   - degree:        string (free text; common values in DEGREE_OPTIONS below)
 *   - pronouns:      string (from PRONOUN_OPTIONS — simple 3-option dropdown,
 *                    matching the Add/Edit Patient form)
 *   - npi:           string (optional; 10-digit National Provider Identifier)
 *   - licenses:      array of { state, number } (optional; clinicians often hold
 *                    licenses in multiple states — telehealth across state lines).
 *                    `state` is a 2-letter US state code from US_STATE_OPTIONS;
 *                    `number` is free text (format varies by state/board).
 *
 * Completeness rule (used for onboarding prompt + dashboard card):
 *   firstName AND lastName both present (non-empty).
 *
 * Display name fallback chain:
 *   "{firstName} {lastName}[, {degree}]"  →  user.displayName  →  user.email  →  "Clinician"
 *
 * See: docs/updates/2026-04-20-user-profile-fields-for-patient-sharing.md
 *      docs/updates/2026-04-21-shared-record-show-note-author.md
 */

// Ben's degree list. Free text on save — these seed the dropdown.
export const DEGREE_OPTIONS = [
  "MD",
  "DO",
  "PhD",
  "PsyD",
  "MSW",
  "LCSW",
  "MSN",
  "DNP",
  "RN",
  "PA",
  "Other",
];

// Pronouns — matches the Add/Edit Patient dropdown (utils/AddEditPatient.js).
// Display labels are handled at render time via PRONOUN_LABELS.
export const PRONOUN_OPTIONS = [
  "he/him",
  "she/her",
  "they/them",
];

export const PRONOUN_LABELS = {
  "he/him": "He/Him",
  "she/her": "She/Her",
  "they/them": "They/Them",
};

// US states + DC + territories — used by the optional licenseState dropdown.
// Stored as 2-letter code; rendered with full name.
export const US_STATE_OPTIONS = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" }, { code: "PR", name: "Puerto Rico" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

// Light NPI validation — exactly 10 digits. Empty/blank is allowed (optional field).
export function isValidNpi(value) {
  const v = (value || "").trim();
  if (!v) return true;
  return /^\d{10}$/.test(v);
}

/**
 * Normalize a `licenses` array for save:
 *   - trim state + number on each row
 *   - drop rows where both fields are empty
 *   - dedupe by state+number pair (keep first)
 * Returns `[]` if input isn't an array.
 */
export function normalizeLicenses(licenses) {
  if (!Array.isArray(licenses)) return [];
  const seen = new Set();
  const out = [];
  for (const row of licenses) {
    if (!row || typeof row !== "object") continue;
    const state = (row.state || "").trim().toUpperCase();
    const number = (row.number || "").trim();
    if (!state && !number) continue;
    const key = `${state}|${number}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ state, number });
  }
  return out;
}

/**
 * Migration helper: read either the new `licenses[]` array or the legacy
 * single `licenseState` / `licenseNumber` fields and return a normalized array
 * for the form to seed from.
 */
export function readLicensesFromUserData(data) {
  if (!data) return [];
  if (Array.isArray(data.licenses) && data.licenses.length > 0) {
    return normalizeLicenses(data.licenses);
  }
  const legacyState = (data.licenseState || "").trim().toUpperCase();
  const legacyNumber = (data.licenseNumber || "").trim();
  if (legacyState || legacyNumber) {
    return [{ state: legacyState, number: legacyNumber }];
  }
  return [];
}

export function isProfileComplete(userData) {
  if (!userData) return false;
  const first = (userData.firstName || "").trim();
  const last = (userData.lastName || "").trim();
  return first.length > 0 && last.length > 0;
}

export function formatUserDisplayName(userData) {
  if (!userData) return "Clinician";
  const first = (userData.firstName || "").trim();
  const last = (userData.lastName || "").trim();
  const name = [first, last].filter(Boolean).join(" ");
  if (name) {
    const degree = (userData.degree || "").trim();
    return degree ? `${name}, ${degree}` : name;
  }
  if (userData.displayName) return userData.displayName;
  if (userData.email) return userData.email;
  return "Clinician";
}
